'use client';
import { DownloadIcon } from '@chakra-ui/icons';
import { Box, Container, Flex, Heading, useDisclosure } from '@chakra-ui/react';
import { usePathname } from 'next/navigation';
import React, { useState } from 'react';
import { useCallback } from 'react';

import { logBrochureClick, sendBrochureRequest, sendWhatsAppBrochureRequest } from '@/services/brochure.service';
import { capitalizeFirstLetter } from '@/services/string-format.service';

import Styles from './Brochure.module.scss';
import BrochureEmailModal from './BrochureEmailModal';

type BrochureProps = {
  model: string;
  brochure: string;
  additional_brochure?:string | null;
  variant?: 'default' | 'compact';
  alwaysShowDeliveryOptions?: boolean;
};

const Brochure = ({ model, brochure, additional_brochure, variant = 'default', alwaysShowDeliveryOptions = false,}: BrochureProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedBrochure, setSelectedBrochure] = useState('');
  const [selectedBrochureLabel, setSelectedBrochureLabel] = useState('Brochure');
  const pathname = usePathname();
  const pageUrl = typeof window !== 'undefined' ? `${window.location.origin}${pathname}` : '';

  const handleOpenModal = useCallback(
    async (
      e: React.MouseEvent<HTMLButtonElement>,
      brochureAsset: string,
      brochureLabel: string,
    ) => {
      e.preventDefault();

      setSelectedBrochure(brochureAsset);
      setSelectedBrochureLabel(brochureLabel);

      try {
        await logBrochureClick({
          model,
          pageUrl,
          brochureAsset,
        });
      } catch (error) {
        console.error('Error logging brochure click:', error);
      } finally {
        onOpen();
      }
    },
    [model, pageUrl, onOpen],
  );

  const handleSubmitEmail = useCallback(
    async (name: string, email: string) => {
      if (!selectedBrochure) {
        throw new Error('No brochure selected');
      }

      const additionalBrochure =
        selectedBrochure === brochure
          ? additional_brochure
          : brochure;

      await sendBrochureRequest({
        name,
        email,
        model,
        pageUrl,
        brochureAsset: selectedBrochure,
        additionalBrochureAsset: additionalBrochure || undefined,
      });
    },
    [model, pageUrl, selectedBrochure, brochure, additional_brochure],
  );

  const handleSubmitWhatsApp = useCallback(
    async (name: string, whatsappNumber: string) => {
      if (!selectedBrochure) {
        throw new Error('No brochure selected');
      }

      const additionalBrochure =
      selectedBrochure === brochure
        ? additional_brochure
        : brochure;

      await sendWhatsAppBrochureRequest({
        name,
        whatsappNumber,
        model,
        pageUrl,
        brochureAsset: selectedBrochure,
        additionalBrochureAsset: additionalBrochure || undefined,
      });
    },
    [selectedBrochure, model, pageUrl, brochure, additional_brochure],
  );

  const handleCloseModal = useCallback(() => {
    onClose();
    setSelectedBrochure('');
    setSelectedBrochureLabel('Brochure');
  }, [onClose]);

  const displayModel = capitalizeFirstLetter(model);

  if (variant === 'compact') {
    return (
      <>
        {brochure && (
          <button
            type="button"
            className="btnTextLink"
            onClick={(e) => handleOpenModal(e, brochure, 'Brochure')}
            style={{
              backgroundColor: 'transparent',
              padding: 0,
              border: 0,
              color: '#05141f',
              fontSize: '14px',
              lineHeight: '1.2',
              fontWeight: 600,
            }}
          >
            Brochure
          </button>
        )}

        {additional_brochure && (
          <button
            type="button"
            className="btnTextLink"
            onClick={(e) =>
              handleOpenModal(e, additional_brochure, 'Accessories Brochure')
            }
            style={{
              backgroundColor: 'transparent',
              padding: 0,
              border: 0,
              color: '#05141f',
              fontSize: '14px',
              lineHeight: '1.2',
              fontWeight: 600,
            }}
          >
            Accessories Brochure
          </button>
        )}

       <BrochureEmailModal
          isOpen={isOpen}
          onClose={handleCloseModal}
          model={displayModel}
          brochureLabel={selectedBrochureLabel}
          alwaysShowDeliveryOptions={alwaysShowDeliveryOptions}
          onSubmitEmail={handleSubmitEmail}
          onSubmitWhatsApp={handleSubmitWhatsApp}
        />
      </>
    );
  }

  return (
    <Box className={Styles.brochureWrapper}>
      <Container
        maxW="1328px"
        w="100%"
        px={{ base: 6, lg: 0 }}
        py={10}
      >
        <Flex
          direction={{ base: 'column', md: 'row' }}
          alignItems="center"
          justifyContent={{ base: 'flex-start', md: 'center' }}
          gap={{ base: 4, md: 8 }}
        >
          <Box className={Styles.headingElement}>
            <Heading
              as="p"
              fontWeight="bold"
              className={Styles.titleMd}
              color="white"
            >
              Want to explore the Kia {displayModel} offline?
            </Heading>
          </Box>

          <Flex
            direction={{ base: 'column', md: 'row' }}
            alignItems="center"
            justifyContent="center"
            gap={4}
          >
            {brochure && (
              <button
                type="button"
                className={`${Styles.btnOutlineWhites} btnOutlineWhites`}
                onClick={(e) => handleOpenModal(e, brochure, 'Brochure')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DownloadIcon
                  width={16}
                  height={16}
                  style={{ marginRight: '8px' }}
                />
                Brochure
              </button>
            )}

            {additional_brochure && (
              <button
                type="button"
                className={`${Styles.btnOutlineWhites} btnOutlineWhites`}
                onClick={(e) =>
                  handleOpenModal(e, additional_brochure, 'Accessories Brochure')
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DownloadIcon
                  width={16}
                  height={16}
                  style={{ marginRight: '8px' }}
                />
                Accessories Brochure
              </button>
            )}
          </Flex>
        </Flex>
      </Container>

      <BrochureEmailModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        model={displayModel}
        brochureLabel={selectedBrochureLabel}
        alwaysShowDeliveryOptions={alwaysShowDeliveryOptions}
        onSubmitEmail={handleSubmitEmail}
        onSubmitWhatsApp={handleSubmitWhatsApp}
      />
    </Box>
  );
};

export default Brochure;